import type { PurchaseOrder, PurchaseOrderStatus, RequestStatus, UserRole } from './types';
import { emptyWellInfo } from './procurementRequest';

export function isProcurementStaffRole(role: UserRole): boolean {
  return role === 'ProcurementManager' || role === 'ProcurementSpecialist';
}

/** Procurement specialists/managers and Administrator can perform procurement workflows (not IT Support). */
export function canPerformProcurementStaffActions(role: UserRole): boolean {
  return (
    role === 'ProcurementManager' || role === 'ProcurementSpecialist' || role === 'Administrator'
  );
}

/** View PO registry, wells, vendors (includes Administrator and IT Support). */
export function canViewProcurementModules(role: UserRole): boolean {
  return (
    isProcurementStaffRole(role) ||
    role === 'Administrator' ||
    role === 'ITSupport' ||
    role === 'OperationsManager'
  );
}

/** Load delivery-address directory for request forms (API GET /api/delivery-addresses). */
export function canLoadDeliveryAddressDirectory(role: UserRole): boolean {
  return (
    role === 'Requestor' ||
    role === 'RegionalManager' ||
    canViewProcurementModules(role)
  );
}

/** Inventory + material transfers (API: /api/inventory, /api/material-transfers). */
export function canViewInventoryAndTransfers(role: UserRole): boolean {
  return (
    role === 'Requestor' ||
    role === 'RegionalManager' ||
    canViewProcurementModules(role)
  );
}

export function isAdministrator(role: UserRole): boolean {
  return role === 'Administrator';
}

/** Admin is its own module (Users / Roles / Regions / …), not a Procurement tab. */
export function canOpenAdminModule(role: UserRole): boolean {
  return isAdministrator(role);
}

/** True when the unioned permission list contains the code (case-insensitive). */
export function hasPermission(
  permissions: string[] | undefined,
  code: string,
  fallbackRole?: UserRole
): boolean {
  if (permissions && permissions.length > 0) {
    const wanted = code.trim().toLowerCase();
    return permissions.some((p) => p.trim().toLowerCase() === wanted);
  }
  if (fallbackRole === 'Administrator') {
    return true;
  }
  if (fallbackRole === 'ContractAdmin') {
    const c = code.trim().toLowerCase();
    return (
      c.startsWith('contract:') ||
      c === 'doi:read' ||
      c === 'party:read' ||
      c === 'field:read' ||
      c === 'tract:read' ||
      c === 'masterwell:read' ||
      c === 'profile:read' ||
      c === 'region:read' ||
      c === 'product:read'
    );
  }
  if (fallbackRole === 'DivisionOrder') {
    const c = code.trim().toLowerCase();
    return (
      c === 'doi:read' ||
      c === 'doi:update' ||
      c === 'contract:read' ||
      c === 'contract:create' ||
      c === 'contract:update' ||
      c === 'profile:read' ||
      c === 'region:read' ||
      c === 'product:read'
    );
  }
  if (fallbackRole === 'Controller') {
    const c = code.trim().toLowerCase();
    return (
      c === 'period:read' ||
      c === 'period:update' ||
      c === 'doi:read' ||
      c === 'excalibur:import' ||
      c === 'profile:read' ||
      c === 'region:read' ||
      c === 'product:read' ||
      c === 'product:update'
    );
  }
  return false;
}

export function permissionsOf(auth: { role: UserRole; profile?: { permissions?: string[] } }): string[] | undefined {
  return auth.profile?.permissions;
}

export function canReadAssistant(
  role: UserRole,
  permissions?: string[]
): boolean {
  return hasPermission(permissions, 'agent:read', role);
}

/** Contracts / Commercial / Accounting / Master. */
export function canViewBooksModules(role: UserRole, permissions?: string[]): boolean {
  return (
    hasPermission(permissions, 'contract:read', role) ||
    hasPermission(permissions, 'party:read', role) ||
    hasPermission(permissions, 'doi:read', role) ||
    hasPermission(permissions, 'period:read', role)
  );
}

export function isReadOnlyRole(role: UserRole): boolean {
  return role === 'ITSupport';
}

/** Matches API policy WellDirectoryWriters. */
export function isWellDirectoryWriter(role: UserRole): boolean {
  return (
    role === 'Requestor' ||
    role === 'ProcurementManager' ||
    role === 'ProcurementSpecialist' ||
    role === 'Administrator'
  );
}

export function requestDetailHeading(request: { requestId?: string | null }): string {
  const id = request.requestId?.trim();
  return id ? `Request ${id}` : 'Request details';
}

export function formatStatus(status: RequestStatus): string {
  const labels: Record<RequestStatus, string> = {
    draft: 'Draft',
    pendingApproval: 'Pending approval',
    procurementReview: 'Procurement review',
    sourcing: 'Sourcing',
    orderPlaced: 'Order placed',
    procured: 'Procured',
    complete: 'Complete',
    rejected: 'Rejected'
  };
  return labels[status] ?? status;
}

export function formatMoney(value: number): string {
  return new Intl.NumberFormat(undefined, { style: 'currency', currency: 'USD' }).format(value);
}

export function formatPoStatus(status: PurchaseOrderStatus): string {
  const labels: Record<PurchaseOrderStatus, string> = {
    created: 'Created',
    paid: 'Paid'
  };
  return labels[status] ?? status;
}

export function purchaseOrderLineItemsSubtotal(po: Pick<PurchaseOrder, 'lineItems'>): number {
  return po.lineItems.reduce((sum, line) => sum + line.lineTotal, 0);
}

export function purchaseOrderGrandTotal(
  po: Pick<PurchaseOrder, 'lineItems' | 'freightAmount' | 'miscellaneousFeeAmount'>
): number {
  return (
    purchaseOrderLineItemsSubtotal(po) +
    (po.freightAmount ?? 0) +
    (po.miscellaneousFeeAmount ?? 0)
  );
}

/** Parses optional USD input; blank → 0. */
export function parseOptionalUsdAmount(
  input: string
): { ok: true; amount: number } | { ok: false; message: string } {
  const t = input.trim();
  if (t === '') {
    return { ok: true, amount: 0 };
  }
  const n = Number(t);
  if (Number.isNaN(n)) {
    return { ok: false, message: 'Enter a valid dollar amount.' };
  }
  if (n < 0) {
    return { ok: false, message: 'Amount cannot be negative.' };
  }
  return { ok: true, amount: n };
}

/** PO line unit price: required, must be a number ≥ 0. */
export function parseNonNegativeUnitPrice(
  input: string
): { ok: true; amount: number } | { ok: false; message: string } {
  const t = input.trim();
  if (t === '') {
    return { ok: false, message: 'Unit price is required.' };
  }
  const n = Number(t);
  if (Number.isNaN(n)) {
    return { ok: false, message: 'Enter a valid unit price.' };
  }
  if (n < 0) {
    return { ok: false, message: 'Unit price cannot be negative.' };
  }
  return { ok: true, amount: n };
}

export function normalizePurchaseOrder(raw: PurchaseOrder): PurchaseOrder {
  const wellInfo = raw.wellInfo ?? emptyWellInfo();
  const freight = raw.freightAmount;
  const misc = raw.miscellaneousFeeAmount;
  return {
    ...raw,
    freightAmount: typeof freight === 'number' && !Number.isNaN(freight) ? freight : 0,
    miscellaneousFeeAmount: typeof misc === 'number' && !Number.isNaN(misc) ? misc : 0,
    status: raw.status === 'paid' ? 'paid' : 'created',
    wellInfo: {
      ...wellInfo,
      region: wellInfo.region ?? '',
      location: {
        state: wellInfo.location?.state ?? '',
        country: wellInfo.location?.country ?? ''
      }
    }
  };
}
